import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Server, 
  Plus, 
  Loader2,
  AlertCircle,
  Trash2,
  Edit,
  CheckCircle2,
  XCircle,
  TestTube
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ZabbixConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    token: '',
    verify_ssl: true
  });
  const { getAuthHeader } = useAuthStore();

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zabbix-config`, {
        headers: getAuthHeader()
      });
      setConfigs(response.data);
    } catch (err) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingConfig) {
        await axios.put(`${API}/zabbix-config/${editingConfig.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Configuração atualizada');
      } else {
        await axios.post(`${API}/zabbix-config`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Configuração criada');
      }
      setDialogOpen(false);
      setFormData({ name: '', url: '', token: '', verify_ssl: true });
      setEditingConfig(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      url: config.url,
      token: config.token,
      verify_ssl: config.verify_ssl !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta configuração?')) return;

    try {
      await axios.delete(`${API}/zabbix-config/${configId}`, {
        headers: getAuthHeader()
      });
      toast.success('Configuração excluída');
      fetchConfigs();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const handleSetPrimary = async (configId) => {
    try {
      await axios.post(`${API}/zabbix-config/${configId}/set-primary`, {}, { headers: getAuthHeader() });
      toast.success('Definido como primário');
      fetchConfigs();
    } catch (err) { toast.error('Erro'); }
  };

  const handleToggleActive = async (configId) => {
    try {
      await axios.post(`${API}/zabbix-config/${configId}/toggle-active`, {}, { headers: getAuthHeader() });
      fetchConfigs();
    } catch (err) { toast.error('Erro'); }
  };

  const handleTest = async (configId) => {
    setTesting(configId);
    try {
      const response = await axios.post(`${API}/zabbix-config/${configId}/test`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success(`Conexão OK! Zabbix v${response.data.version}`);
      } else {
        toast.error(`Falha: ${response.data.error}`);
      }
    } catch (err) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(null);
    }
  };

  const openNewDialog = () => {
    setEditingConfig(null);
    setFormData({ name: '', url: '', token: '', verify_ssl: true });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="zabbix-config-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Configurações Zabbix API</h2>
          <p className="text-sm text-muted-foreground">
            Configure as conexões com seus servidores Zabbix
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} data-testid="add-config-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nova Configuração
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>
                  {editingConfig ? 'Editar Configuração' : 'Nova Configuração'}
                </DialogTitle>
                <DialogDescription>
                  Configure os dados de acesso à API do Zabbix
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Produção"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="config-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="url">URL da API</Label>
                  <Input
                    id="url"
                    placeholder="http://zabbix.exemplo.com/api_jsonrpc.php"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    required
                    data-testid="config-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    URL completa do endpoint da API JSON-RPC do Zabbix
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="token">Token de API</Label>
                  <Input
                    id="token"
                    type="password"
                    placeholder="Seu token de API do Zabbix"
                    value={formData.token}
                    onChange={(e) => setFormData(prev => ({ ...prev, token: e.target.value }))}
                    required
                    data-testid="config-token-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Gere um token em Zabbix → Administração → API Tokens
                  </p>
                </div>

                <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
                  <div className="space-y-1">
                    <Label htmlFor="verify_ssl" className="text-sm">Validar certificado SSL</Label>
                    <p className="text-xs text-muted-foreground">
                      Mantenha ativado para URLs HTTPS com certificado válido. Desative se o Zabbix
                      usa certificado self-signed ou é acessado por <code>IP:porta</code>.
                    </p>
                  </div>
                  <Switch
                    id="verify_ssl"
                    checked={formData.verify_ssl}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, verify_ssl: v }))}
                    data-testid="config-verify-ssl-switch"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-config-btn">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {configs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={config.id} data-testid={`config-row-${config.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{config.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {config.url}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {config.is_primary && (
                          <Badge className="bg-blue-500 text-white text-xs">Primário</Badge>
                        )}
                        {config.is_active ? (
                          <Badge variant="default" className="bg-green-500 text-xs">Ativo</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Inativo</Badge>
                        )}
                        {config.verify_ssl === false && (
                          <Badge variant="outline" className="text-xs" title="Validação de certificado SSL desativada">SSL off</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(config.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {!config.is_primary && config.is_active && (
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleSetPrimary(config.id)}>
                            Primário
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleToggleActive(config.id)} title={config.is_active ? 'Desativar' : 'Ativar'}>
                          {config.is_active ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-gray-400" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleTest(config.id)} disabled={testing === config.id}>
                          {testing === config.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(config)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(config.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">Nenhuma configuração</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure a API do Zabbix para começar a extrair métricas
              </p>
              <Button onClick={openNewDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Configuração
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Como obter o Token da API</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <ol className="list-decimal list-inside space-y-1">
            <li>Acesse seu Zabbix como administrador</li>
            <li>Vá em <strong>Administração → Geral → API Tokens</strong></li>
            <li>Clique em <strong>Criar API Token</strong></li>
            <li>Selecione um usuário com permissões adequadas</li>
            <li>Copie o token gerado e cole aqui</li>
          </ol>
          <p className="mt-4">
            <strong>Nota:</strong> O token precisa ter permissões de leitura nos grupos de hosts que deseja monitorar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
